import React, { useState, useEffect } from 'react';
import GmailComposeModal from './GmailComposeModal';

// ── Main Modal ────────────────────────────────────────────────────────────────

export default function ContractModal({ deal, dealId, onClose, onGenerated, offMarketMode }) {
  const [step, setStep]       = useState('entry');     // 'entry' | 'loading' | 'summary' | 'error'
  const [terms]               = useState({ property_address: deal?.address || deal?.property_address || '' });
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState(null);

  // Template awareness (migration 032): the org must have configured its own Frontside Purchase
  // Contract. When it has, the modal renders that template's mapped fields (Variable/Pre-filled
  // editable; MLS-Metric auto for MLS deals, manual for off-market; Fixed locked). When it hasn't
  // (or the fields aren't mapped yet), we prompt to set it up — there is no shared/bundled fallback.
  const [tplMode, setTplMode]     = useState('loading');   // 'loading' | 'template' | 'no-template'
  const [template, setTemplate]   = useState(null);        // { id, name, status }
  const [tplFields, setTplFields] = useState([]);          // mapped field defs (with db ids)
  const [tplRow, setTplRow]       = useState(null);        // the slot's list row (for the not-mapped hint)
  const isMlsDeal = !offMarketMode && !!(deal && deal.mls_id);

  // ── Duplicate-offer guard ───────────────────────────────────────────────────
  // Before an agent writes an offer, check whether anyone on the team already offered on this
  // property. WARN, never block: the second offer is sometimes the right call (the first died, the
  // first agent handed it over), so this exists to make the collision visible, not to prevent it.
  // null = still checking, [] = nobody has, [..] = show the interstitial first.
  const [priorOffers, setPriorOffers] = useState(null);
  const [ackDuplicate, setAckDuplicate] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const addr = deal?.address || deal?.property_address || '';
    if (!addr && !deal?.mls_id) { setPriorOffers([]); return; }
    const qs = new URLSearchParams({ address: addr });
    if (deal?.zip) qs.set('zip', String(deal.zip));
    if (deal?.mls_id) qs.set('mls_id', String(deal.mls_id));
    // The deal being offered on is not its own prior offer.
    if (dealId) qs.set('exclude_deal_id', String(dealId));
    fetch(`/api/deals/offer-history?${qs}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!cancelled) setPriorOffers(Array.isArray(d.offers) ? d.offers : []); })
      // Fail OPEN: a lookup problem must not stand between an agent and their contract.
      .catch(() => { if (!cancelled) setPriorOffers([]); });
    return () => { cancelled = true; };
  }, [deal, dealId]);

  // Hold the entry form back only while there is something to warn about and it hasn't been seen.
  const showDuplicateWarning = step === 'entry' && !ackDuplicate && priorOffers != null && priorOffers.length > 0;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetch('/api/contracts/templates', { credentials: 'include' }).then(r => r.json());
        const row = Array.isArray(list) ? list.find(t => t.slot === 'frontside_purchase') : null;
        if (cancelled) return;
        setTplRow(row || null);
        if (row && row.exists && row.status === 'ready' && row.id) {
          const fx = await fetch(`/api/contracts/templates/${row.id}/fields`, { credentials: 'include' }).then(r => r.json());
          if (cancelled) return;
          setTemplate({ id: row.id, name: row.name, status: row.status });
          setTplFields(fx.fields || []);
          setTplMode('template');
        } else if (!cancelled) {
          setTplMode('no-template');
        }
      } catch { if (!cancelled) setTplMode('no-template'); }
    })();
    return () => { cancelled = true; };
  }, []);

  async function generate(t) {
    setStep('loading');
    try {
      // If no dealId yet, pass full listing data so backend creates a new Under Contract deal
      const body = { deal_id: dealId || null, terms: t };
      if (!dealId && deal && offMarketMode) {
        body.off_market_listing = {
          id: deal._off_market_id,
          address: deal.address, city: deal.city, state: deal.state, zip: deal.zip,
          beds: deal.beds, baths: deal.baths, sqft: deal.sqft, year_built: deal.year_built,
          asking_price: deal.list_price, ai_arv: deal.ai_arv,
          max_offer: deal.max_offer, repair_cost: deal.repair_cost,
          notes: deal.description,
          photo_url: deal.photo_url, lat: deal.lat, lng: deal.lng,
          contact_name: deal.contact_name, contact_email: deal.contact_email, contact_phone: deal.contact_phone,
        };
      } else if (!dealId && deal && deal.mls_id) {
        body.mls_listing = {
          mls_id: deal.mls_id,
          address: deal.address,
          city: deal.city, state: deal.state, zip: deal.zip,
          beds: deal.beds, baths: deal.baths, sqft: deal.sqft, year_built: deal.year_built,
          list_price: deal.list_price, ai_arv: deal.ai_arv,
          ai_score: deal.opportunity_score || deal.ai_score,
          max_offer: deal.max_offer, repair_cost: deal.repair_cost,
          assignment_fee: deal.assignment_fee, days_on_market: deal.days_on_market,
          description: deal.description,
          photos: deal.photos, photo_url: deal.photo_url || (deal.photos && deal.photos[0]),
          lat: deal.lat, lng: deal.lng,
          agent_name: deal.agent_name, agent_email: deal.agent_email, agent_phone: deal.agent_phone,
          owner_name: deal.owner_name, parcel_number: deal.parcel_number,
          county: deal.county, legal_description: deal.legal_description, subdivision: deal.subdivision, tax_lot: deal.tax_lot, tax_block: deal.tax_block,
        };
      }
      const r = await fetch('/api/contracts/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (data.error) throw new Error(data.error);
      setResult(data);
      setStep('summary');
      if (onGenerated) onGenerated(data);
    } catch (err) {
      setError(err.message);
      setStep('error');
    }
  }

  // Template mode: values are keyed by field id; the backend resolves MLS-Metric + Fixed itself.
  const handleTemplateSubmit = (fieldValues) => generate({
    property_address: deal?.address || deal?.property_address || '',
    zip: deal?.zip || '',
    field_values: fieldValues,
  });

  const wide = step === 'entry' && tplMode === 'template';
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 16, width: '100%', maxWidth: wide ? 640 : step === 'summary' ? 580 : 460, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <ModalHeader step={step} onClose={onClose} />

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px 28px' }}>
          {showDuplicateWarning &&
            <DuplicateOfferView offers={priorOffers} address={deal?.address || deal?.property_address}
              onCancel={onClose} onContinue={() => setAckDuplicate(true)} />
          }
          {!showDuplicateWarning && step === 'entry' && tplMode === 'loading' && <LoadingView label="Loading contract template…" />}
          {!showDuplicateWarning && step === 'entry' && tplMode === 'template' &&
            <TemplateForm deal={deal} template={template} fields={tplFields} isMlsDeal={isMlsDeal}
              onSubmit={handleTemplateSubmit} />
          }
          {!showDuplicateWarning && step === 'entry' && tplMode === 'no-template' &&
            <NoTemplateView tplRow={tplRow} onClose={onClose} />
          }
          {step === 'loading' &&
            <LoadingView label={tplMode === 'template' ? 'Filling your contract template…' : undefined} />
          }
          {step === 'summary' &&
            <SummaryView result={result} terms={terms} dealId={dealId} agentEmail={deal?.agent_email} onClose={onClose} />
          }
          {step === 'error' &&
            <ErrorView error={error} onBack={() => setStep('entry')} />
          }
        </div>
      </div>
    </div>
  );
}

// ── Duplicate-offer interstitial ──────────────────────────────────────────────
// Shown once, before the contract form, when someone on the team has already offered on this
// property. Warn-and-proceed: "Write the offer anyway" is a normal primary action, not a dare.
//
// Renders WHO, WHEN and WHAT CAME OF IT, and nothing else. There is no offer amount here and none in
// the payload behind it (see GET /api/deals/offer-history) — the point is to surface the collision,
// not to let one agent price against another's number.
function DuplicateOfferView({ offers, address, onCancel, onContinue }) {
  const fmt = (d) => {
    if (!d) return 'an unknown date';
    const dt = new Date(d);
    return isNaN(dt) ? 'an unknown date'
      : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };
  const many = offers.length > 1;
  return (
    <div>
      <div style={{ padding: 16, borderRadius: 10, background: 'rgba(224, 165, 38,0.10)', border: '1px solid rgba(224, 165, 38,0.35)', marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold)', marginBottom: 8 }}>
          {many ? `${offers.length} offers have already been made on this property` : 'Someone has already offered on this property'}
        </div>
        {address && (
          <div style={{ fontSize: 12.5, color: 'var(--gray-300)', marginBottom: 12 }}>{address}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {offers.slice(0, 6).map((o, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline',
                                  padding: '8px 10px', borderRadius: 7, background: 'rgba(0,0,0,0.14)' }}>
              <div style={{ fontSize: 13, color: 'var(--white)', fontWeight: 600 }}>
                {o.is_you ? 'You' : o.agent_name}
                <span style={{ fontWeight: 400, color: 'var(--gray-300)' }}>
                  {' '}offered on {fmt(o.offered_at)}
                </span>
              </div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--gray-300)', whiteSpace: 'nowrap' }}>
                {o.status}{o.is_past_deal ? ' · past' : ''}
              </div>
            </div>
          ))}
          {offers.length > 6 && (
            <div style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>+ {offers.length - 6} more</div>
          )}
        </div>

        <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 12, lineHeight: 1.5 }}>
          Offer amounts and terms aren't shown. Check with {many ? 'them' : (offers[0] && offers[0].is_you ? 'your records' : offers[0].agent_name)} before writing another offer.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onCancel}
          style={{ flex: 1, padding: '11px 18px', borderRadius: 10, border: '1px solid var(--navy-border)',
                   background: 'transparent', color: 'var(--gray-300)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          Cancel
        </button>
        <button onClick={onContinue}
          style={{ flex: 1, padding: '11px 18px', borderRadius: 10, border: 'none',
                   background: 'var(--teal)', color: '#FAF7F2', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          Write the offer anyway
        </button>
      </div>
    </div>
  );
}

// Shown when the org hasn't finished setting up its Frontside Purchase Contract — either nothing is
// uploaded, or a PDF is uploaded but its fields aren't mapped yet. Links to Settings.
function NoTemplateView({ tplRow }) {
  const uploadedButUnmapped = tplRow && tplRow.exists && tplRow.status !== 'ready';
  return (
    <div>
      <div style={{ padding: 16, borderRadius: 10, background: 'rgba(20, 80, 74,0.06)', border: '1px solid rgba(20, 80, 74,0.2)', marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)', marginBottom: 6 }}>
          {uploadedButUnmapped ? 'Finish mapping your contract fields' : 'Set up your purchase contract'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.6 }}>
          {uploadedButUnmapped
            ? 'Your Frontside Purchase Contract PDF is uploaded, but its fields aren’t mapped yet. Map them so feebe knows what to fill.'
            : 'Upload your own purchase contract PDF and map its fields once. After that, property data auto-fills from the MLS on every deal.'}
        </div>
      </div>
      <a href={uploadedButUnmapped && tplRow?.slot ? `/settings/templates/${tplRow.slot}` : '/settings'}
        style={{ display: 'block', textAlign: 'center', padding: '11px 16px', borderRadius: 10, background: 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
        {uploadedButUnmapped ? 'Map fields' : 'Go to Contract Templates'}
      </a>
    </div>
  );
}

const prettify = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// Deal-term input kinds (kept in sync with backend services/dealTermsValues.js). 'value' = typed
// amount, 'days'/'hours' = offset from the draft date (prints the computed date), 'date' = a calendar
// date that defaults to the draft date, 'computed' = derived.
const DEAL_TERM_INPUT = { purchase_price: 'value', earnest_money: 'value', cash_at_closing: 'computed', close_date: 'days', acceptance_deadline: 'hours', offer_date: 'date' };
const DEAL_TERM_DEFAULT = { close_date: '14', acceptance_deadline: '48' };
// Earnest money in 'percent' mode is auto-computed from the purchase price (locked); in 'preset' mode
// it's a prefilled, editable amount.
const termKind = (f) => {
  if (!f || f.field_type !== 'deal_term') return null;
  if (f.term_key === 'earnest_money') return f.term_mode === 'percent' ? 'computed' : 'value';
  return DEAL_TERM_INPUT[f.term_key];
};
const money0 = (n) => (n == null || isNaN(Number(n)) ? '' : '$' + Math.round(Number(n)).toLocaleString());
const numOnly = (v) => Number(String(v == null ? '' : v).replace(/[^0-9.-]/g, ''));

// Previews of the dates the server will print, in the org's market timezone (org_settings.
// market_timezone — the server falls back to America/Phoenix the same way).
const FALLBACK_TZ = 'America/Phoenix';
const safeTz = (tz) => { try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return tz; } catch { return FALLBACK_TZ; } };
const longDate = (d, tz) => new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: tz }).format(d);
const previewClose = (days, tz) => longDate(new Date(Date.now() + Math.round(Number(days) || 0) * 86400000), tz);
const previewAccept = (hrs, tz) => {
  const d = new Date(Date.now() + Math.round(Number(hrs) || 0) * 3600000);
  const abbr = new Intl.DateTimeFormat('en-US', { hour: 'numeric', timeZoneName: 'short', timeZone: tz }).formatToParts(d).find(p => p.type === 'timeZoneName')?.value || '';
  return `${longDate(d, tz)} at 10:00 PM${abbr ? ' ' + abbr : ''}`;
};
// Today in the market, as 'YYYY-MM-DD' (what <input type="date"> and the server both speak).
const todayYmd = (tz) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: tz }).formatToParts(new Date()).map(x => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
};
// A date-only string is a day, not an instant — never put it through new Date() (UTC midnight).
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const fmtYmd = (ymd, mode) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!m) return '';
  return mode === 'numeric' ? `${m[2]}/${m[3]}/${m[1]}` : `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
};

// A field is "locked" in the generate form (resolved server-side, no user input): checkboxes, Fixed,
// MLS-Metric on an MLS deal (auto-filled), and the computed cash-due-at-closing deal term. Everything
// else is an editable input.
function fieldLocked(f, isMlsDeal) {
  return f.element === 'checkbox' || f.field_type === 'fixed' || (f.field_type === 'mls_metric' && isMlsDeal)
    || (f.field_type === 'deal_term' && termKind(f) === 'computed');
}

// Group a template's fields so a value that recurs in the contract — address, purchase price, etc.
// appear in several spots — is entered/resolved ONCE and fanned out to every box. Grouping key, in
// priority order: an explicit label (case-insensitive), else the field's binding (same MLS metric or
// same Fixed source), else the field's own id. The locked/editable class is appended so a Fixed (or
// MLS) field never merges with an editable field that happens to share a label — that would render the
// group locked and silently drop the editable field's value (an unfixable 422 at generation).
function groupFields(fields, isMlsDeal) {
  const map = new Map();
  for (const f of fields) {
    const label = (f.label || '').trim();
    let key, display;
    // Deal-term fields group by their term (all "close date" spots share one input) regardless of label.
    if (f.field_type === 'deal_term' && f.term_key) { key = 'term:' + f.term_key; display = label || prettify(f.term_key); }
    else if (label) { key = 'lbl:' + label.toLowerCase(); display = label; }
    else if (f.field_type === 'mls_metric' && f.mls_key) { key = 'mls:' + f.mls_key; display = prettify(f.mls_key); }
    else if (f.field_type === 'fixed' && f.settings_key) { key = 'set:' + f.settings_key; display = prettify(f.settings_key); }
    else { key = 'id:' + f.id; display = f.acroform_name || 'Unlabeled field'; }
    key += fieldLocked(f, isMlsDeal) ? '|L' : '|E';
    if (!map.has(key)) map.set(key, { key, label: display, type: f.field_type, rep: f, fields: [] });
    map.get(key).fields.push(f);
  }
  return Array.from(map.values());
}

// The five terms an agent looks for first on any offer. Found by the deal term they are mapped to when
// there is one, else by label (inspection period and title company are usually Pre-filled or Fixed
// fields, named however the org's own form names them). First match wins.
const KEY_TERMS = [
  { id: 'price',      label: 'Price',             match: (g) => g.rep.term_key === 'purchase_price' || /purchase price|sales? price/i.test(g.label) },
  { id: 'emd',        label: 'Earnest money',     match: (g) => g.rep.term_key === 'earnest_money' || /earnest|\bemd\b/i.test(g.label) },
  { id: 'close',      label: 'Close date',        match: (g) => g.rep.term_key === 'close_date' || /clos(e|ing) date|coe\b/i.test(g.label) },
  { id: 'inspection', label: 'Inspection period', match: (g) => /inspection|option period|due diligence|feasibility/i.test(g.label) },
  { id: 'title',      label: 'Title company',     match: (g) => g.fields.some(f => f.settings_key === 'title_company') || /title (co|company|agency)|escrow (co|company|agent)/i.test(g.label) },
];

const SECTION_STYLE = {
  required: { bg: 'rgba(224, 165, 38, 0.08)', border: '1px solid rgba(224, 165, 38, 0.45)' },
  done:     { bg: 'rgba(46, 158, 143, 0.07)', border: '1px solid rgba(46, 158, 143, 0.4)' },
  terms:    { bg: 'transparent',             border: '1px solid var(--navy-border)' },
  prefill:  { bg: 'transparent',             border: '1px solid var(--navy-border)' },
};

// A called function, NOT a component: defining a component inside TemplateForm would give it a new
// identity every render, remounting its inputs and dropping focus on every keystroke.
function formSection({ kind, title, hint, right, children }) {
  return (
    <div style={{ marginBottom: 14, padding: 14, borderRadius: 12, background: SECTION_STYLE[kind].bg, border: SECTION_STYLE[kind].border }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: hint ? 2 : 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>{title}</div>
        {right}
      </div>
      {hint && <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 10 }}>{hint}</div>}
      {children}
    </div>
  );
}

// ── Template Form ─────────────────────────────────────────────────────────────
// Renders a configured template's fields in the order an agent works them:
//   1. Needs to be filled — highlighted; everything the server will refuse to generate without
//      (Variable, MLS-Metric on an off-market deal, the purchase price). Membership is decided once,
//      so a field doesn't jump sections as it is typed into — it turns green instead.
//   2. Offer terms — the per-offer deal terms, already defaulted (earnest, close date, offer date…).
//   3. Pre-filled — Pre-filled / Fixed / MLS / checkboxes. Collapsed by default; filled automatically.
// A key-terms strip at the top shows price, EMD, close date, inspection period and title company at a
// glance, and jumps to each one. A group's entered value applies to every box carrying that label.
export function TemplateForm({ deal, template, fields, isMlsDeal, onSubmit, submitLabel = 'Generate Contract' }) {
  const groups = groupFields(fields, isMlsDeal);

  // Workspace defaults — so a Pre-filled / Fixed field bound to one (title company, buyer entity…)
  // shows the value that will actually print, and so date previews use the market's timezone.
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/org-settings', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : {}))
      .then(s => {
        if (cancelled) return;
        const flat = { ...(s || {}) };
        for (const c of (s && s.custom) || []) flat[`custom:${c.id}`] = c.value;
        setSettings(flat);
      })
      .catch(() => { if (!cancelled) setSettings({}); });
    return () => { cancelled = true; };
  }, []);
  const tz = safeTz((settings && settings.market_timezone) || FALLBACK_TZ);
  const settingVal = (k) => (k && settings && settings[k] != null ? String(settings[k]) : '');

  const isLocked   = (g) => fieldLocked(g.rep, isMlsDeal);
  // Required if ANY member needs input: Variable, MLS-Metric on an off-market deal, or the purchase
  // price. Pre-filled and date/offset terms have defaults; earnest is computed or preset-defaulted.
  const isRequired = (g) => g.fields.some(f => f.element !== 'checkbox' && (
    f.field_type === 'variable' ||
    (f.field_type === 'mls_metric' && !isMlsDeal) ||
    (f.field_type === 'deal_term' && f.term_key === 'purchase_price')
  ));
  const sectionOf = (g) => isRequired(g) ? 'required' : (g.type === 'deal_term' ? 'terms' : 'prefill');

  const initial = {};
  for (const g of groups) {
    if (isLocked(g)) continue;
    const k = termKind(g.rep);
    if (k === 'days' || k === 'hours') initial[g.key] = g.rep.default_value || DEAL_TERM_DEFAULT[g.rep.term_key] || '';
    else if (k === 'date') initial[g.key] = '';   // blank = the draft date; filled once the timezone is known
    else if (g.rep.term_key === 'earnest_money') initial[g.key] = g.rep.default_value || '';   // preset amount
    else initial[g.key] = g.type === 'prefilled' ? (g.rep.default_value || '') : '';
  }
  const [values, setValues] = useState(initial);
  const [showErr, setShowErr] = useState(false);
  const [prefillOpen, setPrefillOpen] = useState(false);
  const set = (key, v) => setValues(prev => ({ ...prev, [key]: v }));

  // Once settings arrive: default the offer date to the market's today, and show the workspace
  // default in any Pre-filled box bound to one — only where nothing has been typed yet. Submitting the
  // shown value prints exactly what the server would have filled from settings anyway.
  useEffect(() => {
    if (!settings) return;
    setValues(prev => {
      const next = { ...prev };
      for (const g of groups) {
        if (isLocked(g) || String(next[g.key] ?? '') !== '') continue;
        if (termKind(g.rep) === 'date') next[g.key] = todayYmd(tz);
        else if (g.type === 'prefilled') {
          const bound = g.fields.map(f => settingVal(f.settings_key)).find(Boolean);
          if (bound) next[g.key] = bound;
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const filled = (g) => String(values[g.key] ?? '').trim() !== '';
  const missing = groups.filter(g => isRequired(g) && !filled(g));

  // Live previews for the computed deal terms (purchase → earnest → cash-due). Mirrors the server math
  // so the locked fields show a value as the user types.
  const purchaseGroup = groups.find(g => g.rep.term_key === 'purchase_price');
  const earnestGroup  = groups.find(g => g.rep.term_key === 'earnest_money');
  const purchaseAmt = purchaseGroup ? numOnly(values[purchaseGroup.key]) : NaN;
  let earnestAmt = NaN;
  if (earnestGroup) {
    earnestAmt = earnestGroup.rep.term_mode === 'percent'
      ? ((!isNaN(purchaseAmt) && earnestGroup.rep.default_value) ? purchaseAmt * Number(earnestGroup.rep.default_value) / 100 : NaN)
      : numOnly(values[earnestGroup.key]);
  }
  const cashAmt = (!isNaN(purchaseAmt) ? purchaseAmt : 0) - (!isNaN(earnestAmt) ? earnestAmt : 0);

  // What a locked field will print, in words the agent recognises.
  const lockedText = (g) => {
    if (g.rep.element === 'checkbox') return '☑ ' + (CHECK_RULE_LABELS[g.rep.check_rule] || 'Auto-checked');
    if (g.rep.term_key === 'cash_at_closing') return `Purchase − earnest${!isNaN(purchaseAmt) && purchaseAmt ? ` = ${money0(Math.max(0, cashAmt))}` : ''}`;
    if (g.rep.term_key === 'earnest_money') return `${g.rep.default_value || '?'}% of price${!isNaN(earnestAmt) ? ` = ${money0(earnestAmt)}` : ''}`;
    if (g.type === 'mls_metric') return 'From the MLS listing';
    const fixed = g.fields.map(f => (f.fixed_value != null && String(f.fixed_value).trim() !== '') ? String(f.fixed_value) : settingVal(f.settings_key)).find(Boolean);
    return fixed || (settings ? 'Blank — set it in Settings' : 'Workspace default');
  };
  // What a group will print right now — for the key-terms strip.
  const displayValue = (g) => {
    if (isLocked(g)) {
      if (g.rep.term_key === 'earnest_money') return !isNaN(earnestAmt) ? money0(earnestAmt) : '';
      const t = lockedText(g);
      return /^Blank|^Workspace default$/.test(t) ? '' : t;
    }
    const v = values[g.key];
    const k = termKind(g.rep);
    if (k === 'days') return previewClose(v, tz);
    if (k === 'hours') return previewAccept(v, tz);
    if (k === 'date') return fmtYmd(v, g.rep.term_mode);
    if (k === 'value' && String(v ?? '').trim()) return money0(numOnly(v));
    return String(v ?? '').trim();
  };

  // Key terms → the group that carries each, in strip order.
  const used = new Set();
  const keyTerms = KEY_TERMS.map(kt => {
    const g = groups.find(x => !used.has(x.key) && kt.match(x));
    if (g) used.add(g.key);
    return g ? { ...kt, g } : null;
  }).filter(Boolean);
  const keyIds = new Set(keyTerms.map(k => k.g.key));

  const refs = React.useRef({});
  const jumpTo = (g) => {
    if (sectionOf(g) === 'prefill') setPrefillOpen(true);
    setTimeout(() => {
      const el = refs.current[g.key];
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = el.querySelector('input');
      if (input) input.focus({ preventScroll: true });
    }, 30);
  };

  const submit = () => {
    if (missing.length) { setShowErr(true); jumpTo(missing[0]); return; }
    // Fan each group's value out to every field id it covers; locked groups are resolved server-side.
    const out = {};
    for (const g of groups) {
      if (isLocked(g)) continue;
      const v = values[g.key] ?? '';
      for (const f of g.fields) out[f.id] = v;
    }
    onSubmit(out);
  };

  // Key terms first within each section, then the template's own order.
  const inSection = (s) => groups.filter(g => sectionOf(g) === s)
    .sort((a, b) => (keyIds.has(b.key) ? 1 : 0) - (keyIds.has(a.key) ? 1 : 0));
  const required = inSection('required');
  const terms    = inSection('terms');
  const prefill  = inSection('prefill');
  const blanks   = prefill.filter(g => !isLocked(g) && !filled(g)).length;

  const inputStyle = (bad) => ({ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: `1px solid ${bad ? '#B85450' : 'var(--navy-border)'}`, background: 'var(--navy)', color: 'var(--white)', fontSize: 12, outline: 'none' });

  const renderField = (g) => {
    const locked = isLocked(g);
    const req = isRequired(g);
    const done = filled(g);
    const bad = showErr && req && !done;
    const kind = termKind(g.rep);
    const count = g.fields.length;
    const isKey = keyIds.has(g.key);
    return (
      <div key={g.key} ref={el => { refs.current[g.key] = el; }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: isKey ? 'var(--white)' : 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'flex', justifyContent: 'space-between', gap: 6 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {req && <span style={{ color: done ? 'var(--teal)' : 'var(--gold)', marginRight: 4 }}>{done ? '✓' : '●'}</span>}
            {g.label}{count > 1 ? <span style={{ color: 'var(--gray-500)', fontWeight: 500 }}> · {count} spots</span> : null}
          </span>
          {!req && <TypeTag t={g.type} auto={g.type === 'mls_metric' && isMlsDeal} checkbox={g.rep.element === 'checkbox'} />}
        </div>
        {locked ? (
          <div style={{ padding: '8px 10px', borderRadius: 6, border: '1px dashed var(--navy-border)', background: 'var(--navy)', color: 'var(--gray-300)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {lockedText(g)}
          </div>
        ) : (kind === 'days' || kind === 'hours') ? (
          <>
            <input type="number" min="0" value={values[g.key] ?? ''} onChange={e => set(g.key, e.target.value)}
              placeholder={kind === 'days' ? '14' : '48'} style={inputStyle(false)} />
            <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 4 }}>
              {kind === 'days' ? `days from today → ${previewClose(values[g.key], tz)}` : `hours to accept → ${previewAccept(values[g.key], tz)}`}
            </div>
          </>
        ) : kind === 'date' ? (
          <>
            <input type="date" value={values[g.key] ?? ''} onChange={e => set(g.key, e.target.value)} style={inputStyle(false)} />
            <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 4 }}>
              {values[g.key] && values[g.key] !== todayYmd(tz) ? `prints ${fmtYmd(values[g.key], g.rep.term_mode)}` : 'today — filled in automatically'}
            </div>
          </>
        ) : (
          <input value={values[g.key] ?? ''} onChange={e => set(g.key, e.target.value)}
            placeholder={req ? 'Required' : 'Blank — prints empty'}
            style={inputStyle(bad)} />
        )}
      </div>
    );
  };

  const grid = (gs) => <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>{gs.map(renderField)}</div>;

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)' }}>{deal?.address || deal?.property_address || 'Unknown property'}</div>
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
          Template: <span style={{ color: 'var(--gray-300)' }}>{template?.name || 'Frontside Purchase Contract'}</span>
          {isMlsDeal
            ? <span style={{ color: 'var(--teal)' }}> · property data from the MLS</span>
            : <span style={{ color: 'var(--gold)' }}> · off-market — property fields are typed in</span>}
        </div>
      </div>

      {groups.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>This template has no fields mapped yet.</div>
      )}

      {keyTerms.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(keyTerms.length, 5)}, minmax(0, 1fr))`, gap: 8, marginBottom: 14 }}>
          {keyTerms.map(kt => {
            const raw = displayValue(kt.g);
            // Money terms read as money even when mapped as a plain Variable field ("410000" → $410,000).
            const v = (kt.id === 'price' || kt.id === 'emd') && /^\$?[\d,]+(\.\d+)?$/.test(raw) ? money0(numOnly(raw)) : raw;
            const needs = isRequired(kt.g) && !filled(kt.g);
            return (
              <button key={kt.id} type="button" onClick={() => jumpTo(kt.g)}
                style={{ textAlign: 'left', padding: '8px 10px', borderRadius: 8, cursor: 'pointer', minWidth: 0,
                         background: 'var(--navy)', border: `1px solid ${needs ? 'rgba(224, 165, 38, 0.6)' : 'var(--navy-border)'}` }}>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{kt.label}</div>
                <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 3, color: needs ? 'var(--gold)' : v ? 'var(--white)' : 'var(--gray-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {needs ? 'Needs value' : (v || 'Blank')}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {required.length > 0 && (
        formSection({ kind: missing.length ? 'required' : 'done',
          title: missing.length ? `Fill these to generate · ${missing.length} left` : '✓ Required fields filled',
          hint: missing.length ? 'The contract can’t be generated until each of these has a value.' : null,
          children: grid(required) })
      )}

      {terms.length > 0 && (
        formSection({ kind: 'terms', title: 'Offer terms', hint: 'Defaults are filled in — change anything for this offer.', children: grid(terms) })
      )}

      {prefill.length > 0 && (
        formSection({ kind: 'prefill', title: 'Pre-filled',
          hint: prefillOpen ? 'Filled automatically from your workspace defaults, the listing, and the template.' : `${prefill.length} field${prefill.length === 1 ? '' : 's'} filled automatically${blanks ? ` · ${blanks} blank` : ''}.`,
          right: <button type="button" onClick={() => setPrefillOpen(o => !o)}
            style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
            {prefillOpen ? 'Hide' : 'Review'}</button>,
          children: prefillOpen ? grid(prefill) : null })
      )}

      <button onClick={submit} style={{ width: '100%', marginTop: 4, padding: '11px 16px', borderRadius: 10, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                        background: missing.length ? 'var(--gray-500)' : 'var(--teal)' }}>
        {missing.length ? `Fill ${missing.length} required field${missing.length === 1 ? '' : 's'} to generate` : submitLabel}
      </button>
    </div>
  );
}

// Friendly labels for checkbox rules (kept in sync with backend CHECKBOX_RULES).
const CHECK_RULE_LABELS = {
  always: 'Always checked',
  never: 'Never checked',
  built_before_1978: 'Checked if built before 1978',
};

function TypeTag({ t, auto, checkbox }) {
  if (checkbox) return <span style={{ fontSize: 8, color: '#9333EA', fontWeight: 700, letterSpacing: '0.04em' }}>CHECKBOX</span>;
  const map = {
    fixed:      { label: 'Fixed',    color: '#14504A' },
    prefilled:  { label: 'Editable', color: '#3B82F6' },
    variable:   { label: 'Required', color: '#B85450' },
    mls_metric: { label: auto ? 'MLS' : 'Manual', color: '#2E9E8F' },
    deal_term:  { label: 'Deal Term', color: '#C08A2D' },
  };
  const m = map[t] || map.variable;
  return <span style={{ fontSize: 8, color: m.color, fontWeight: 700, letterSpacing: '0.04em' }}>{m.label.toUpperCase()}</span>;
}

function ModalHeader({ step, onClose }) {
  const titles = {
    entry:    'Generate Contract',
    review:   'Review Contract Terms',
    loading:  'Generating Contract…',
    summary:  'Contract Generated',
    error:    'Generation Failed',
  };
  return (
    <div style={{ padding: '20px 28px 16px', borderBottom: '1px solid var(--navy-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>{titles[step]}</div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
    </div>
  );
}

// ── Loading View ──────────────────────────────────────────────────────────────

export function LoadingView({ label }) {
  return (
    <div style={{ padding: '60px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <div style={{ width: 44, height: 44, border: '3px solid var(--navy-border)', borderTopColor: 'var(--teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <div style={{ fontSize: 13, color: 'var(--gray-300)', fontWeight: 600 }}>{label || 'Filling contract form…'}</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Summary View ──────────────────────────────────────────────────────────────

function SummaryView({ result, terms, dealId, agentEmail, onClose }) {
  const [composeOpen, setComposeOpen] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentDetails, setSentDetails] = useState(null);   // { status, followup_id }

  // Prefer the deal_id from the just-completed generation result, fall back to prop
  const effectiveDealId = result?.deal_id || dealId;

  // Send to Agent opens the Gmail composer with this contract attached. The user reviews the draft and
  // presses Send themselves; it goes out from their own mailbox and replies track onto the deal.
  function handleSendToAgent() {
    if (!effectiveDealId) return alert('Deal id missing — cannot send.');
    setComposeOpen(true);
  }


  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, padding: '12px 16px', background: 'rgba(74, 124, 89,0.08)', border: '1px solid rgba(74, 124, 89,0.25)', borderRadius: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#4A7C59', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>✓</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#4A7C59' }}>
            {result.created_new_deal ? 'Deal added to pipeline (Offer Sent)' : 'Contract ready to send'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>{result.filename}</div>
        </div>
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Key Terms</div>

      <div style={{ background: 'var(--navy)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
        {[
          ['Property', terms.property_address],
          ['Document', result.filename],
        ].map(([k, v], i, arr) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: i < arr.length - 1 ? '1px solid rgba(232, 227, 218, 0.7)' : 'none', fontSize: 12 }}>
            <span style={{ color: 'var(--gray-500)' }}>{k}</span>
            <span style={{ color: 'var(--gray-300)', fontWeight: 500, textAlign: 'right', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={async () => {
          try {
            const r = await fetch(`/api/contracts/download/${encodeURIComponent(result.filename)}`);
            if (!r.ok) throw new Error('HTTP ' + r.status);
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = result.filename;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          } catch (err) { alert('Download failed: ' + err.message); }
        }}
          style={{ flex: 1, minWidth: 160, padding: '10px 14px', borderRadius: 8, background: 'rgba(20, 80, 74,0.12)', border: '1px solid rgba(20, 80, 74,0.4)', color: 'var(--teal)', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <DownloadIcon /> Download PDF
        </button>
        <button onClick={handleSendToAgent} disabled={emailSent || !effectiveDealId}
          style={{ flex: 1, minWidth: 160, padding: '10px 14px', borderRadius: 8,
            background: emailSent ? 'rgba(74, 124, 89,0.15)' : 'rgba(20, 80, 74,0.12)',
            border: `1px solid ${emailSent ? '#4A7C59' : 'rgba(20, 80, 74,0.4)'}`,
            color: emailSent ? '#4A7C59' : '#14504A',
            fontSize: 13, fontWeight: 700,
            cursor: (emailSent || !effectiveDealId) ? 'not-allowed' : 'pointer',
            opacity: !effectiveDealId ? 0.5 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <EmailIcon /> {emailSent ? 'Sent ✓' : 'Send to Agent'}
        </button>
      </div>

      {sentDetails && (
        <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
          background: sentDetails.status === 'sent' ? 'rgba(74, 124, 89,0.08)' : 'rgba(224, 165, 38,0.08)',
          border: '1px solid ' + (sentDetails.status === 'sent' ? 'rgba(74, 124, 89,0.35)' : 'rgba(224, 165, 38,0.35)'),
          color: sentDetails.status === 'sent' ? '#4A7C59' : 'var(--gold)' }}>
          {sentDetails.status === 'sent' ? '✓ Sent from your Gmail with the contract attached' : '⚠ Logged but not sent (Gmail sending is off)'}
          {sentDetails.followup_id ? ' · follow-up reminder scheduled' : ''}
          {effectiveDealId && <a href={`/deals/${effectiveDealId}#email`} style={{ marginLeft: 8, color: 'var(--teal)', textDecoration: 'underline', fontWeight: 500 }}>View on deal →</a>}
        </div>
      )}

      {composeOpen && (
        <GmailComposeModal
          deal={{ id: effectiveDealId, address: terms.property_address, agent_email: agentEmail }}
          mode="new"
          preselectFilename={result.filename}
          onClose={() => setComposeOpen(false)}
          onSent={(d) => { setEmailSent(true); setSentDetails(d); }}
        />
      )}

      {/* E-signature is a separate mapping layer (place signature/date blocks in the builder) that
          ships with DocuSign integration. Until then, in-app send-for-signature is hidden so we never
          create an envelope with no signature tabs — download the PDF and send it for signatures
          outside the app. */}
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--gray-500)', textAlign: 'center' }}>
        Download the PDF to sign yourself or send for signatures outside the app. In-app e-signature is coming soon.
      </div>

      <button onClick={onClose} style={{ width: '100%', marginTop: 12, padding: '8px 14px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-500)', fontSize: 12, cursor: 'pointer' }}>
        Close
      </button>
    </div>
  );
}

// ── Error View ────────────────────────────────────────────────────────────────

function ErrorView({ error, onBack }) {
  return (
    <div>
      <div style={{ padding: '14px', borderRadius: 10, background: 'rgba(184, 84, 80,0.08)', border: '1px solid rgba(184, 84, 80,0.3)', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#B85450', marginBottom: 4 }}>Generation failed</div>
        <div style={{ fontSize: 12, color: 'var(--gray-300)' }}>{error}</div>
      </div>
      <button onClick={onBack} style={{ width: '100%', padding: '10px', borderRadius: 8, background: 'var(--teal)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Try Again</button>
    </div>
  );
}

// ── Icons ────────────────────────────────────────────────────────────────────

function DownloadIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
}
function EmailIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
}
